import React from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine,
} from "recharts";
import { pairLegs, legLabel } from "../lib/pairs";

const LONG = "#1f3a93";   // 롱 레그
const SHORT = "#9aa0a6";  // 숏 레그

const fmtYear = t => new Date(t).getFullYear().toString().slice(2);
const box = { background: "#fff", borderRadius: 8, padding: "8px 10px 4px", border: "1px solid #f0f0f0", minWidth: 0 };
const title = { fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 2 };

function MiniChart({ children, height = 150 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
}

const sgn = (v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;
const zColor = (v) => (v >= 0 ? "#c0392b" : "#0a7c59");   // +는 롱레그 고평가(숏 방향)

// z = 가격비 항 + 레벨 항. 둘이 반대 부호로 크면 합계 z만으로는 판단이 안 선다.
function ZBreakdown({ result, shortName }) {
  const { z, zRatio, zLevel, beta } = result;
  const conflict = zRatio * zLevel < 0 && Math.min(Math.abs(zRatio), Math.abs(zLevel)) > 1;
  return (
    <div
      title={`z = [ln(L/S) − 평균]/σ  +  [(1−β)·ln(S) − 평균]/σ\n`
        + `가격비: 두 종목 가격비가 자기 평균 대비 얼마나 벌어졌나\n`
        + `레벨: β=${beta.toFixed(2)}≠1 이라 남는 ${shortName} 가격수준 기여 (β=1이면 0)`}
      style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8,
        fontSize: 11, color: "#666", marginBottom: 10, padding: "5px 9px",
        background: conflict ? "#fffaf0" : "#f7f7f7",
        border: `1px solid ${conflict ? "#f0dcae" : "#eee"}`, borderRadius: 6,
      }}>
      <span style={{ fontWeight: 700, color: "#444" }}>z {sgn(z)}</span>
      <span style={{ color: "#bbb" }}>=</span>
      <span>가격비 <b style={{ color: zColor(zRatio) }}>{sgn(zRatio)}</b></span>
      <span style={{ color: "#bbb" }}>+</span>
      <span>레벨 <b style={{ color: zColor(zLevel) }}>{sgn(zLevel)}</b></span>
      {conflict && (
        <span style={{ color: "#b8860b", fontWeight: 600 }}>
          ⚠ 두 항이 반대 — {Math.abs(zLevel) > Math.abs(zRatio) ? `${shortName} 가격수준이` : "가격비가"} 신호를 지배
        </span>
      )}
    </div>
  );
}

export default function PairSpreadCard({ pair, result, onDelete }) {
  const { longs, shorts } = pairLegs(pair);
  const longName = legLabel(longs), shortName = legLabel(shorts);
  const signalColor = result?.signal === "SHORT Spread" ? "#c0392b"
    : result?.signal === "LONG Spread" ? "#0a7c59" : "#888";

  return (
    <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 12, padding: 14, marginBottom: 16 }}>
      {/* 헤더 */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          <span style={{ color: LONG }}>🔺 L: {longName}</span>
          <span style={{ color: "#bbb", margin: "0 8px" }}>vs</span>
          <span style={{ color: "#c0392b" }}>🔻 S: {shortName}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {result && (
            <>
              <span style={{ fontSize: 12, color: "#888" }}>β {result.beta.toFixed(2)} · ρ {result.logCorr.toFixed(2)}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: signalColor }}>
                z {result.z >= 0 ? "+" : ""}{result.z.toFixed(2)}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: signalColor, borderRadius: 6, padding: "2px 8px" }}>
                {result.signal}
              </span>
            </>
          )}
          <button onClick={() => onDelete(pair)} title="페어 삭제"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", fontSize: 16 }}>✕</button>
        </div>
      </div>

      {/* z 분해 — 가격비 항과 레벨 항이 반대로 끌 때 합계만 보면 안 보인다 */}
      {result && result.zRatio != null && <ZBreakdown result={result} shortName={shortName} />}

      {!result ? (
        <div style={{ padding: 24, textAlign: "center", color: "#aaa", fontSize: 13 }}>
          데이터 부족 — 시세 조회 실패 또는 공통 거래일이 너무 적습니다.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
          {/* 가격 */}
          <div style={box}>
            <div style={title}>▶ 가격</div>
            <MiniChart>
              <LineChart data={result.rows} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <XAxis dataKey="t" tickFormatter={fmtYear} tick={{ fontSize: 9, fill: "#aaa" }} minTickGap={40} />
                <YAxis yAxisId="l" tick={{ fontSize: 9, fill: LONG }} width={38} domain={["auto", "auto"]} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9, fill: SHORT }} width={38} domain={["auto", "auto"]} />
                <Tooltip labelFormatter={t => new Date(t).toLocaleDateString()} formatter={(v, n) => [Math.round(v).toLocaleString(), n === "p1" ? longName : shortName]} />
                <Line yAxisId="l" dataKey="p1" stroke={LONG} dot={false} strokeWidth={1.3} isAnimationActive={false} />
                <Line yAxisId="r" dataKey="p2" stroke={SHORT} dot={false} strokeWidth={1.3} isAnimationActive={false} />
              </LineChart>
            </MiniChart>
          </div>

          {/* Log 가격 */}
          <div style={box}>
            <div style={title}>▶ Log 가격</div>
            <MiniChart>
              <LineChart data={result.rows} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <XAxis dataKey="t" tickFormatter={fmtYear} tick={{ fontSize: 9, fill: "#aaa" }} minTickGap={40} />
                <YAxis yAxisId="l" tick={{ fontSize: 9, fill: LONG }} width={34} domain={["auto", "auto"]} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9, fill: SHORT }} width={34} domain={["auto", "auto"]} />
                <Tooltip labelFormatter={t => new Date(t).toLocaleDateString()} formatter={(v, n) => [v.toFixed(3), n === "ln1" ? longName : shortName]} />
                <Line yAxisId="l" dataKey="ln1" stroke={LONG} dot={false} strokeWidth={1.3} isAnimationActive={false} />
                <Line yAxisId="r" dataKey="ln2" stroke={SHORT} dot={false} strokeWidth={1.3} isAnimationActive={false} />
              </LineChart>
            </MiniChart>
          </div>

          {/* 스프레드 */}
          <div style={box}>
            <div style={title}>▶ 스프레드 (잔차 ±1σ·±2σ)</div>
            <MiniChart>
              <LineChart data={result.rows} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <XAxis dataKey="t" tickFormatter={fmtYear} tick={{ fontSize: 9, fill: "#aaa" }} minTickGap={40} />
                <YAxis tick={{ fontSize: 9, fill: "#888" }} width={38} domain={["auto", "auto"]} />
                <Tooltip labelFormatter={t => new Date(t).toLocaleDateString()} formatter={v => [v.toFixed(4), "spread"]} />
                <ReferenceLine y={0} stroke="#333" strokeWidth={1} />
                <ReferenceLine y={result.sigma} stroke="#4a90d9" strokeDasharray="4 3" />
                <ReferenceLine y={-result.sigma} stroke="#4a90d9" strokeDasharray="4 3" />
                <ReferenceLine y={result.sigma * 2} stroke="#c0392b" strokeDasharray="2 3" />
                <ReferenceLine y={-result.sigma * 2} stroke="#c0392b" strokeDasharray="2 3" />
                <Line dataKey="spread" stroke="#1f3a93" dot={false} strokeWidth={1.4} isAnimationActive={false} />
              </LineChart>
            </MiniChart>
            <div style={{ fontSize: 9, color: "#aaa", textAlign: "right", paddingRight: 4 }}>
              위=롱 강세 / 아래=숏 강세
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
